import axios from 'axios';
import { Modal, message } from 'antd';
import useAuthStore from '../stores/authStore';

const client = axios.create({
  baseURL: '/api/v1',
  timeout: 30000,
  // 不预设 Content-Type：axios 自动识别 FormData(multipart) / 对象(JSON) / 字符串(text)
});

client.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 防抖：并发请求同时 401 时只弹一次被踢提示
let kickPromptShown = false;

// 2026-09-27：全局网络/服务异常提示（主流用语）——后端未启动/断网时页面此前无任何系统提示
//  · 无响应（断网/后端 3000 未启动）→ "无法连接到服务器，请检查网络或稍后重试"
//  · 请求超时 → "请求超时，请稍后重试"
//  · 5xx → "服务暂时不可用，请稍后重试"
export function resolveGlobalErrorNotice(err: any): string | null {
  if (!err?.response) {
    if (err?.code === 'ECONNABORTED' || err?.code === 'ETIMEDOUT') return '请求超时，请稍后重试';
    return '无法连接到服务器，请检查网络或稍后重试';
  }
  const status = Number(err.response.status);
  if (status >= 500) return '服务暂时不可用，请稍后重试';
  return null;
}

// 同类提示 4 秒内只弹一次（避免并发请求刷屏）；antd key 保证覆盖而非堆叠
let lastNoticeAt = 0;
let lastNoticeText = '';
export function notifyGlobalError(err: any): void {
  const text = resolveGlobalErrorNotice(err);
  if (!text) return;
  const now = Date.now();
  if (text === lastNoticeText && now - lastNoticeAt < 4000) return;
  lastNoticeAt = now;
  lastNoticeText = text;
  message.error({ content: text, key: 'global-http-error' });
}

// 2026-09-13 修复：认证类接口的 401 属于业务错误（密码错误/未设置密码等），
// 应交由调用方 catch 弹出提示，不能走"登出+整页跳转"（否则提示被跳转吞掉，用户无感知退回登录页）
const AUTH_ENDPOINTS_NO_REDIRECT = [
  '/auth/login',
  '/auth/sms/login',
  '/auth/sms/verify',
  '/auth/register',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/unlock',
  // S5 客服：widget 换取 supportToken 失败应静默走匿名访客，不触发登出/跳转
  '/auth/support-token',
];

client.interceptors.response.use(
  (res) => res,
  (err) => {
    // 2026-09-27：网络/服务异常全局提示（断网、后端未启动、超时、5xx）
    notifyGlobalError(err);
    if (err.response?.status === 401) {
      const url = String(err.config?.url || '');
      if (AUTH_ENDPOINTS_NO_REDIRECT.some((p) => url.startsWith(p))) {
        return Promise.reject(err);
      }
      const body = err.response.data;
      // 被互踢/手动踢下线：无论是否已弹过提示（防并发重复弹窗），一律不跳转，等用户点"重新登录"
      if (body?.code === 'SESSION_KICKED') {
        if (!kickPromptShown) {
          kickPromptShown = true;
          const t = body.kickedAt ? new Date(body.kickedAt) : null;
          const timeStr = t
            ? `${t.getFullYear()}年${t.getMonth() + 1}月${t.getDate()}日 ${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`
            : '';
          Modal.warning({
            title: '账号在其他设备登录',
            content: timeStr
              ? `您的账号已于 ${timeStr} 在另一台设备登录。如非本人操作，可能存在密码泄露风险，请立即修改密码。`
              : '您的账号已在另一台设备登录。如非本人操作，可能存在密码泄露风险，请立即修改密码。',
            okText: '重新登录',
            onOk: () => {
              kickPromptShown = false;
              useAuthStore.getState().logout();
              window.location.href = '/login';
            },
          });
        }
        return Promise.reject(err);
      }
      // 匿名访问（发布查看器 /s/* 等无登录态场景）：401 不登出、不跳登录，交由调用方降级
      const hadToken =
        !!useAuthStore.getState().token ||
        !!localStorage.getItem('dtwin_token') ||
        !!localStorage.getItem('token');
      if (!hadToken) return Promise.reject(err);
      useAuthStore.getState().logout();
      const currentUrl = window.location.pathname + window.location.search;
      window.location.href = `/login?redirect=${encodeURIComponent(
        window.location.origin + currentUrl,
      )}`;
    }
    return Promise.reject(err);
  },
);

export default client;